import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Bell, TrendingUp, TrendingDown, Mail, ArrowRight, Activity } from 'lucide-react';
import { getUnifiedNotificationStats, getNotificationActivityFeed, getUnifiedNotificationAnalytics } from '../../utils/adminApi';
import { DELIVERY_STATUS_COLORS } from './workflowConfig';
import '../../styles/NotificationOverview.css';

const KpiCard = ({ label, value, trend, suffix = '', icon: Icon, color }) => {
  const isPositive = trend >= 0;
  const trendIsGood = label === 'Bounce Rate' ? !isPositive : isPositive;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <div className="notif-kpi-card" style={{ borderLeftColor: color }}>
      <div className="notif-kpi-icon" style={{ backgroundColor: color + '22', color }}>
        <Icon size={18} />
      </div>
      <div className="notif-kpi-details">
        <div className="notif-kpi-label">{label}</div>
        <div className="notif-kpi-value">{value}{suffix}</div>
        <div className={`notif-kpi-trend ${trendIsGood ? 'positive' : 'negative'}`}>
          <TrendIcon size={12} />
          <span>{Math.abs(trend)}% vs prior</span>
        </div>
      </div>
    </div>
  );
};

const FunnelBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
  return (
    <div className="notif-funnel-row">
      <div className="notif-funnel-label">{label}</div>
      <div className="notif-funnel-bar-track">
        <div className="notif-funnel-bar-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="notif-funnel-value">{count.toLocaleString()} <span className="notif-funnel-pct">({pct}%)</span></div>
    </div>
  );
};

const MiniActivityItem = ({ item }) => {
  const statusColor = DELIVERY_STATUS_COLORS[item.status] || '#6b7280';
  const timeAgo = getRelativeTime(item.createdAt);

  return (
    <div className="notif-mini-activity-item">
      <div className="notif-mini-activity-dot" style={{ backgroundColor: statusColor }} />
      <div className="notif-mini-activity-content">
        <div className="notif-mini-activity-title">
          {item.channel === 'email' ? '📧' : '📱'} {item.title}
          {item.recipient && <span className="notif-mini-activity-recipient"> → {item.recipient}</span>}
        </div>
        <div className="notif-mini-activity-meta">{timeAgo} · {item.status}</div>
      </div>
    </div>
  );
};

function getRelativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const OverviewTab = ({ onTabChange }) => {
  const [stats, setStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    loadData();
  }, [timeRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, activityData, analyticsData] = await Promise.all([
        getUnifiedNotificationStats(timeRange),
        getNotificationActivityFeed({}, { limit: 5, offset: 0 }),
        getUnifiedNotificationAnalytics(timeRange),
      ]);
      setStats(statsData);
      setRecentActivity(activityData.items);
      setDailyData(analyticsData);
    } catch (err) {
      console.error('Error loading overview data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="notif-overview-loading">Loading overview...</div>;
  }

  return (
    <div className="notif-overview">
      <div className="notif-overview-controls">
        {['day', 'week', 'month', 'quarter'].map(range => (
          <button
            key={range}
            className={`notif-time-btn ${timeRange === range ? 'active' : ''}`}
            onClick={() => setTimeRange(range)}
          >
            {range === 'day' ? '24h' : range === 'week' ? '7d' : range === 'month' ? '30d' : '90d'}
          </button>
        ))}
      </div>

      {stats && (
        <div className="notif-kpi-grid">
          <KpiCard label="Sent" value={stats.sent.value.toLocaleString()} trend={stats.sent.trend} icon={Bell} color="#a78bfa" />
          <KpiCard label="Delivered" value={stats.deliveryRate.value} suffix="%" trend={stats.deliveryRate.trend} icon={Activity} color="#22d3ee" />
          <KpiCard label="Open Rate" value={stats.openRate.value} suffix="%" trend={stats.openRate.trend} icon={Mail} color="#fbbf24" />
          <KpiCard label="Click Rate" value={stats.clickRate.value} suffix="%" trend={stats.clickRate.trend} icon={TrendingUp} color="#f472b6" />
          <KpiCard label="Bounce Rate" value={stats.bounceRate.value} suffix="%" trend={stats.bounceRate.trend} icon={TrendingDown} color="#fb923c" />
        </div>
      )}

      <div className="notif-overview-charts">
        {stats && (
          <div className="notif-overview-card notif-funnel-card">
            <h3 className="notif-card-title">Delivery Funnel</h3>
            <div className="notif-funnel">
              <FunnelBar label="Sent" count={stats.funnel.sent} total={stats.funnel.sent} color="#a78bfa" />
              <FunnelBar label="Delivered" count={stats.funnel.delivered} total={stats.funnel.sent} color="#22d3ee" />
              <FunnelBar label="Opened" count={stats.funnel.opened} total={stats.funnel.sent} color="#fbbf24" />
              <FunnelBar label="Clicked" count={stats.funnel.clicked} total={stats.funnel.sent} color="#f472b6" />
              <FunnelBar label="Bounced" count={stats.funnel.bounced} total={stats.funnel.sent} color="#f87171" />
            </div>
          </div>
        )}

        <div className="notif-overview-card notif-activity-card">
          <div className="notif-card-header">
            <h3 className="notif-card-title">Recent Activity</h3>
            <button className="notif-view-all-btn" onClick={() => onTabChange?.('activity')}>
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div className="notif-mini-activity-list">
            {recentActivity.length > 0 ? (
              recentActivity.map(item => <MiniActivityItem key={item.id} item={item} />)
            ) : (
              <div className="notif-mini-activity-empty">No recent activity</div>
            )}
          </div>
        </div>
      </div>

      {dailyData.length > 0 && (
        <div className="notif-overview-card notif-volume-card">
          <h3 className="notif-card-title">Send Volume</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dailyData}>
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111827' }} />
              <Legend />
              <Bar dataKey="emailSent" name="Email" fill="#a78bfa" stackId="sent" radius={[2, 2, 0, 0]} />
              <Bar dataKey="pushSent" name="Push" fill="#22d3ee" stackId="sent" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
